import { useEffect, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface DebugInfo {
  appState: any;
  dbState: any;
  lastActivity: any;
  realTimeStatus: string;
  errors: string[];
}

export function SystemDebugger({ embedded = false }: { embedded?: boolean }) {
  // Add safety check for AppProvider
  let appContext;
  try {
    appContext = useApp();
  } catch (error) {
    console.warn('SystemDebugger: AppProvider not available, component will not render');
    return null;
  }
  
  const { state, likeActivity, commentOnActivity, resetWeeklyStats } = appContext;
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    appState: {},
    dbState: {},
    lastActivity: null,
    realTimeStatus: 'Not connected',
    errors: []
  });
  const [isVisible, setIsVisible] = useState(false);

  // Comprehensive system check
  const runSystemDiagnostics = async () => {
    const errors: string[] = [];
    
    try {
      console.log('🔍 SYSTEM DIAGNOSTICS STARTING...');
      
      // 1. Check app state
      const appStateInfo = {
        isAuthenticated: state.isAuthenticated,
        user: state.user ? { id: state.user.id, name: state.user.name } : null,
        totalPoints: state.totalPoints,
        currentWeekPoints: state.currentWeekPoints,
        activitiesCount: state.activities.length,
        activityCredits: state.activityCredits
      };
      
      console.log('📱 App State:', appStateInfo);
      
      // 2. Check database state
      let dbStateInfo = {};
      if (state.user?.id) {
        try {
          const { data: userStats, error: statsError } = await supabase
            .from('user_stats')
            .select('*')
            .eq('user_id', state.user.id)
            .single();
            
          if (statsError) {
            errors.push(`Database stats error: ${statsError.message}`);
          } else {
            dbStateInfo = {
              totalPoints: userStats.total_points,
              currentWeekPoints: userStats.current_week_points,
              activityCredits: userStats.activity_credits,
              activitiesLoggedToday: userStats.activities_logged_today
            };
          }
          
          console.log('🗃️ DB State:', dbStateInfo);
          
          // 3. Check last activity
          const { data: lastActivity, error: activityError } = await supabase
            .from('activities')
            .select('*')
            .eq('user_id', state.user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();
            
          if (activityError && activityError.code !== 'PGRST116') {
            errors.push(`Last activity error: ${activityError.message}`);
          }
          
          console.log('📋 Last Activity:', lastActivity);
          
        } catch (error) {
          errors.push(`Database connection error: ${error}`);
        }
      }
      
      // 4. Test real-time connection
      let realTimeStatus = 'Testing...';
      try {
        const channel = supabase.channel('test-connection');
        await channel.subscribe();
        realTimeStatus = 'Connected';
        supabase.removeChannel(channel);
      } catch (error) {
        realTimeStatus = `Failed: ${error}`;
        errors.push(`Real-time connection error: ${error}`);
      }
      
      console.log('🔗 Real-time Status:', realTimeStatus);
      
      // 5. Check for common issues
      if (appStateInfo.totalPoints !== (dbStateInfo as any).totalPoints) {
        errors.push('Points mismatch between app and database');
      }
      
      if (!state.isAuthenticated) {
        errors.push('User not authenticated');
      }
      
      if (state.activities.length === 0) {
        errors.push('No activities in app state');
      }
      
      setDebugInfo({
        appState: appStateInfo,
        dbState: dbStateInfo,
        lastActivity: debugInfo.lastActivity,
        realTimeStatus,
        errors
      });
      
      console.log('🔍 DIAGNOSTICS COMPLETE. Errors found:', errors.length);
      
    } catch (error) {
      console.error('❌ Diagnostics failed:', error);
      errors.push(`Diagnostics failed: ${error}`);
    }
  };

  // Test like functionality
  const testLike = () => {
    console.log('🧪 TESTING LIKE FUNCTIONALITY');
    if (state.activities.length > 0) {
      const testActivity = state.activities[0];
      console.log('Testing like on activity:', testActivity.id);
      likeActivity(testActivity.id);
    } else {
      console.log('❌ No activities to test like on');
    }
  };

  // Test comment functionality
  const testComment = () => {
    console.log('🧪 TESTING COMMENT FUNCTIONALITY');
    if (state.activities.length > 0) {
      const testActivity = state.activities[0];
      const testComment = `Test comment ${Date.now()}`;
      console.log('Testing comment on activity:', testActivity.id, 'Comment:', testComment);
      commentOnActivity(testActivity.id, testComment);
    } else {
      console.log('❌ No activities to test comment on');
    }
  };

  // Test weekly reset
  const testWeeklyReset = () => {
    console.log('🧪 TESTING WEEKLY RESET');
    resetWeeklyStats();
  };

  useEffect(() => {
    if (isVisible) {
      runSystemDiagnostics();
    }
  }, [isVisible, state.totalPoints, state.currentWeekPoints]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 w-96 max-h-96 overflow-y-auto z-50">
      <Card className="bg-background border-2 border-primary">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm">
            🔧 System Debugger
            <Button
              onClick={() => setIsVisible(false)}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
            >
              ✕
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs space-y-2">
          {/* Errors */}
          {debugInfo.errors.length > 0 && (
            <div>
              <h4 className="font-semibold text-destructive">Errors ({debugInfo.errors.length})</h4>
              {debugInfo.errors.map((error, i) => (
                <Badge key={i} variant="destructive" className="text-xs mb-1 mr-1">
                  {error}
                </Badge>
              ))}
            </div>
          )}
          
          {/* App vs DB State */}
          <div>
            <h4 className="font-semibold">App vs DB State</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <strong>App:</strong>
                <br />Points: {debugInfo.appState.totalPoints}
                <br />Week: {debugInfo.appState.currentWeekPoints}
                <br />Activities: {debugInfo.appState.activitiesCount}
              </div>
              <div>
                <strong>DB:</strong>
                <br />Points: {debugInfo.dbState.totalPoints}
                <br />Week: {debugInfo.dbState.currentWeekPoints}
                <br />Today: {debugInfo.dbState.activitiesLoggedToday}
              </div>
            </div>
          </div>
          
          {/* Real-time Status */}
          <div>
            <h4 className="font-semibold">Real-time: 
              <Badge variant={debugInfo.realTimeStatus === 'Connected' ? 'default' : 'destructive'} className="ml-1">
                {debugInfo.realTimeStatus}
              </Badge>
            </h4>
          </div>
          
          {/* Test Buttons */}
          <div className="space-y-1">
            <h4 className="font-semibold">Test Functions</h4>
            <div className="flex flex-wrap gap-1">
              <Button onClick={runSystemDiagnostics} size="sm" variant="outline" className="text-xs h-6">
                🔍 Scan
              </Button>
              <Button onClick={testLike} size="sm" variant="outline" className="text-xs h-6">
                ❤️ Like
              </Button>
              <Button onClick={testComment} size="sm" variant="outline" className="text-xs h-6">
                💬 Comment
              </Button>
              <Button onClick={testWeeklyReset} size="sm" variant="outline" className="text-xs h-6">
                🔄 Reset
              </Button>
            </div>
          </div>
          
          {/* User Info */}
          <div>
            <h4 className="font-semibold">User Info</h4>
            <div className="text-xs">
              Auth: <Badge variant={debugInfo.appState.isAuthenticated ? 'default' : 'destructive'}>
                {debugInfo.appState.isAuthenticated ? 'Yes' : 'No'}
              </Badge>
              {debugInfo.appState.user && (
                <>
                  <br />ID: {debugInfo.appState.user.id.slice(0, 8)}...
                  <br />Name: {debugInfo.appState.user.name}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}